import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useConcessionTypes, poolDisplayName } from '../../utils/concessions';

import {
  DocumentArrowDownIcon,
  EyeIcon,
  ArrowPathIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Helper function for date formatting
const formatDate = (date, format) => {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  
  switch (format) {
    case 'MMM dd, yyyy':
      return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
    case 'h:mm a':
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    case 'MMM d, h:mm a':
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    case 'MMM d, yyyy':
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    case 'yyyy-MM-dd':
      return d.toISOString().split('T')[0];
    default:
      return d.toString();
  }
};

const UserHistory = ({ user, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [poolTotals, setPoolTotals] = useState({}); // concessionType -> current balance
  const [timezone] = useState('Pacific/Auckland'); // Fixed to NZ timezone
  const [activeTab, setActiveTab] = useState('history');
  const [activeWallet, setActiveWallet] = useState(null);
  const { types: concessionTypes } = useConcessionTypes();

  // Rendered rows. Shared-to-purse conversion events are shown as a single
  // informational row (grey background) so users who only ever had shared
  // concessions still see where their balance came from. They are excluded from
  // the running-total walk because their delta is already baked into the live
  // balance (seeded in runningTotalAt).
  const visibleHistory = history;

  // Wallets are shown as SEPARATE tables so each purse's running total is never
  // blurred with another's. The legacy 'shared' purse was merged into the
  // primary typed purse at migration, so shared events render in the same table
  // as that purse. The primary purse is the first non-shared typed balance
  // (falling back to '50' for legacy users with no typed purse yet).
  const primaryWallet = useMemo(() => {
    const keys = Object.keys(poolTotals).filter(k => k !== 'shared');
    return keys[0] || '50';
  }, [poolTotals]);

  const normalizedWallet = useCallback((raw) => {
    if (!raw || raw === 'shared') return primaryWallet;
    return raw;
  }, [primaryWallet]);

  // Combined balance for the primary wallet = shared + primary purse (shared is
  // 0 post-migration, so this equals the purse balance for migrated users, and
  // still works for legacy users who only ever had shared).
  const walletBalance = useCallback((walletKey) => {
    if (walletKey === primaryWallet) {
      return (Number(poolTotals['shared'] || 0)) + (Number(poolTotals[primaryWallet] || 0));
    }
    return Number(poolTotals[walletKey] || 0);
  }, [poolTotals, primaryWallet]);

  const walletKeys = useMemo(() => {
    const keys = [...new Set(visibleHistory.map(e => normalizedWallet(e.concessionType)))];
    return keys.sort((a, b) => {
      const pri = (k) => (k === primaryWallet ? -1 : 1);
      return pri(a) - pri(b) || a.localeCompare(b);
    });
  }, [visibleHistory, primaryWallet, normalizedWallet]);

  const walletEvents = useCallback((walletKey) => {
    return visibleHistory.filter(e => normalizedWallet(e.concessionType) === walletKey);
  }, [visibleHistory, normalizedWallet]);

  // Running total of THIS wallet just before `event` occurred, seeded from the
  // wallet's current balance and walking backward through full-pool deltas.
  // Hidden shared-to-purse conversion events are skipped: their delta is already
  // baked into the live balance (the seed), so subtracting it again would
  // double-count it (e.g. a visible "+4 grant" would show 0 instead of 4).
  const runningTotalAt = useCallback((event) => {
    const pool = normalizedWallet(event.concessionType);
    let running = Number(walletBalance(pool));
    if (!Number.isFinite(running)) running = 0;
    const fullIdx = history.indexOf(event);
    for (let i = 0; i < fullIdx && i < history.length; i++) {
      const ev = history[i];
      if (ev.isConversion) continue;
      const evPool = normalizedWallet(ev.concessionType);
      if (evPool !== pool) continue;
      const dPrev = Number(ev.delta || 0);
      if (Number.isFinite(dPrev)) running -= dPrev;
    }
    return running;
  }, [history, normalizedWallet, walletBalance]);

  const walletDisplayName = useCallback((walletKey) => {
    return poolDisplayName(concessionTypes, walletKey);
  }, [concessionTypes]);
  
  // The wallet currently shown in the History table. Defaults to the primary
  // wallet until the user picks another tab.
  const currentWallet = (activeWallet && walletKeys.includes(activeWallet)) ? activeWallet : (walletKeys[0] || primaryWallet);

  // Manual entry form state
  const [manualDescription, setManualDescription] = useState('');
  const [manualDate, setManualDate] = useState(formatDate(new Date(), 'yyyy-MM-dd'));
  const [manualDelta, setManualDelta] = useState('');
  const [manualConcessionType, setManualConcessionType] = useState('50');
  const [manualNotes, setManualNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Helper function to format dates with timezone
  const formatInTimezone = (date, timezone, formatString) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid date';
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(d);
    } catch (error) {
      return formatDate(d, formatString);
    }
  };

  const formatDateOnly = (date, timezone) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid date';
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(d);
    } catch (error) {
      return formatDate(d, 'MMM d, yyyy');
    }
  };

  const formatTimeOnly = (date, timezone) => {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Invalid date';
    
    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(d);
    } catch (error) {
      return formatDate(d, 'h:mm a');
    }
  };


  const fetchUserHistory = useCallback(async () => {
    if (!user || !user._id) {
      console.error('User or user._id is null');
      return;
    }
    
    console.log('Fetching user history for user:', user._id);
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/users/${user._id}/history`);
      console.log('User history response:', response.data);
      setHistory(response.data.history || []);
      // Per-pool current balances. Legacy shared balance lives on user.concessions;
      // typed balances are keyed by concession-type slug. Named pools default to 0.
      const typed = response.data.typedBalances || {};
      const totals = { shared: Number(response.data.user?.concessions || 0) };
      for (const [key, val] of Object.entries(typed)) {
        totals[key] = Number(val || 0);
      }
      setPoolTotals(totals);
      setActiveWallet(null);
    } catch (error) {
      console.error('Error fetching user history:', error);
      toast.error('Failed to fetch user history');
    } finally {
      setLoading(false);
    }
  }, [user]);


  useEffect(() => {
    if (isOpen && user && user._id) {
      fetchUserHistory();
    }
  }, [isOpen, user, fetchUserHistory]);

  // Early return if user is null or modal is not open
  if (!isOpen || !user) {
    return null;
  }

  const exportToCSV = () => {
    if (!visibleHistory.length) {
      toast.error('No history data to export');
      return;
    }

    const csvHeaders = [
      'Date',
      'Time',
      'Event Type',
      'Description',
      'Class Name',
      'Class Date',
      'Class Time',
      'Location',
      'Instructor',
      'Concessions',
      'Status',
      'Details'
    ];

    const csvData = visibleHistory.map(event => [
      formatDateOnly(event.timestamp, timezone),
      formatTimeOnly(event.timestamp, timezone),
      event.type,
      event.description,
      event.className || '',
      event.classDate ? formatDateOnly(event.classDate, timezone) : '',
      event.classTime ? formatTimeOnly(event.classTime, timezone) : '',
      event.location || '',
      event.instructor || '',
      event.concessions || '',
      event.status || '',
      event.details || ''
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${user.firstName}_${user.lastName}_history_${formatDate(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('History exported successfully');
  };

  const formatEventTime = (timestamp) => {
    return formatInTimezone(timestamp, timezone, 'MMM d, yyyy h:mm a');
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    if (!manualDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!manualDate) {
      toast.error('Date is required');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/api/admin/users/${user._id}/history/manual`, {
        description: manualDescription.trim(),
        eventDate: new Date(manualDate).toISOString(),
        delta: manualDelta ? Number(manualDelta) : 0,
        concessionType: manualConcessionType,
        reason: manualNotes.trim() || undefined
      });
      toast.success('Manual entry added successfully');
      // Reset form
      setManualDescription('');
      setManualDate(formatDate(new Date(), 'yyyy-MM-dd'));
      setManualDelta('');
      setManualConcessionType('50');
      setManualNotes('');
      // Switch to history tab and refresh
      setActiveTab('history');
      fetchUserHistory();
    } catch (error) {
      console.error('Error adding manual entry:', error);
      toast.error(error.response?.data?.message || 'Failed to add manual entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteManualEntry = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this manual entry? Any concession impact will be reversed.')) {
      return;
    }
    try {
      await axios.delete(`/api/admin/users/${user._id}/history/manual/${eventId}`);
      toast.success('Manual entry deleted');
      fetchUserHistory();
    } catch (error) {
      console.error('Error deleting manual entry:', error);
      toast.error(error.response?.data?.message || 'Failed to delete manual entry');
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="modal-overlay-mobile"
      onClick={onClose}
    >
      <div 
        className="modal-container-mobile user-history-modal"
        style={{ 
          width: '98vw', 
          maxWidth: '1400px',
          height: '90vh'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header-mobile">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold text-primary m-0">
                User History: {user?.firstName} {user?.lastName}
              </h3>
              <p className="text-sm text-secondary mt-1">
                Complete activity timeline and booking history
              </p>
            </div>
            <button
              onClick={onClose}
              className="user-history-close-btn cursor-pointer text-muted p-2 hover:text-primary transition-colors duration-200 flex-shrink-0"
            >
              ×
            </button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="bookings-modal-tabs">
          <button
            onClick={() => setActiveTab('history')}
            className={`bookings-modal-tab ${activeTab === 'history' ? 'active' : ''}`}
          >
            History ({visibleHistory.length})
          </button>
          <button
            onClick={() => setActiveTab('add')}
            className={`bookings-modal-tab ${activeTab === 'add' ? 'active' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
          >
            <PlusIcon className="h-4 w-4" />
            Add Entry
          </button>
        </div>

        {/* Content */}
        <div className="modal-body-mobile">
          {activeTab === 'history' ? (
            <>
              {loading ? (
                <div className="text-center p-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                  <p className="mt-4 text-secondary">Loading user history...</p>
                </div>
              ) : visibleHistory.length === 0 ? (
                <div className="text-center p-10">
                  <EyeIcon className="h-12 w-12 text-muted mx-auto" />
                  <h3 className="mt-4 text-lg font-medium text-primary">
                    No History Found
                  </h3>
                  <p className="mt-2 text-secondary">
                    This user has no recorded activity yet.
                  </p>
                </div>
              ) : (
                <div className="p-6">
                  {walletKeys.length > 0 && (
                    <>
                      {/* Wallet selector tabs */}
                      <div className="bookings-modal-tabs mb-4">
                        {walletKeys.map((walletKey) => {
                          const name = walletDisplayName(walletKey);
                          const count = walletEvents(walletKey).length;
                          return (
                            <button
                              key={walletKey}
                              onClick={() => setActiveWallet(walletKey)}
                              className={`bookings-modal-tab ${currentWallet === walletKey ? 'active' : ''}`}
                            >
                              {name} ({count})
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-baseline justify-between mb-3">
                        <h4 className="text-base font-semibold text-primary m-0">
                          {walletDisplayName(currentWallet)} Wallet
                        </h4>
                        <span className="text-sm text-secondary">
                          Current balance:{' '}
                          <span className={`font-semibold ${walletBalance(currentWallet) < 0 ? 'text-error' : 'text-primary'}`}>
                            {walletBalance(currentWallet)}
                          </span>
                        </span>
                      </div>
                    </>
                  )}
                  <div
                    className="overflow-x-auto"
                    style={{ overflowX: 'auto', overflowY: 'visible' }}
                  >
                    <table className="w-full border-collapse" style={{ minWidth: '1200px' }}>
                      <thead className="bg-hover">
                        <tr>
                          <th className="table-header table-header-left">
                            Event
                          </th>
                          <th className="table-header table-header-left">
                            Class
                          </th>
                          <th className="table-header table-header-left">
                            Class Date/Time
                          </th>
                          <th className="table-header table-header-center">
                            Concessions (+/-)
                          </th>
                          <th className="table-header table-header-center">
                            Status (after)
                          </th>
                          <th className="table-header table-header-center">
                            Attendance
                          </th>
                          <th className="table-header table-header-right">
                            Date of Event
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                      {walletEvents(currentWallet).map((event, index) => (
                        <tr
                          key={index}
                          className={`border-b border-primary/20 ${event.type === 'manual_entry' ? 'manual-entry-row' : ''} ${event.isConversion ? 'conversion-row' : ''}`}
                        >
                          <td className="table-cell table-cell-left user-history-cell">
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                              <div style={{ flex: 1 }}>
                                <span className="text-sm font-medium text-primary">
                                  {event.description}
                                </span>
                                {event.type === 'manual_entry' && (
                                  <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '10px' }}>
                                    Manually added
                                  </span>
                                )}
                                {event.isConversion && (
                                  <span className="badge badge-info" style={{ marginLeft: '8px', fontSize: '10px' }}>
                                    Shared &rarr; {walletDisplayName(normalizedWallet(event.concessionType))}
                                  </span>
                                )}
                                {(() => {
                                  const d = event.details || '';
                                  // Show notes for manual entries, conversions, and specific system messages
                                  const allow =
                                    event.type === 'manual_entry' ||
                                    event.isConversion ||
                                    d.startsWith('Concessions refunded') ||
                                    d.includes('Class cancelled by admin (recurring schedule)');
                                  return allow && d ? (
                                    <div className="text-xs text-secondary italic mt-1">{d}</div>
                                  ) : null;
                                })()}
                              </div>
                              {event.type === 'manual_entry' && event._id && (
                                <button
                                  onClick={() => handleDeleteManualEntry(event._id)}
                                  className="manual-entry-delete-btn"
                                  title="Delete manual entry"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                      <td className="table-cell table-cell-left user-history-cell">
                        <span className="text-sm text-secondary">
                          {event.className || '-'}
                        </span>
                      </td>
                      <td className="table-cell table-cell-left user-history-cell">
                        <span className="text-sm text-secondary">
                          {event.classDate && event.classTime 
                            ? `${formatDateOnly(event.classDate, timezone)} ${formatTimeOnly(event.classTime, timezone)}`
                            : '-'
                          }
                        </span>
                      </td>
                      <td className="table-cell table-cell-center user-history-cell">
                        {(() => {
                          // Conversion rows re-label existing balance rather than adding
                          // new concessions, so leave the +/- column blank and let the
                          // Status (after) column carry the balance.
                          if (event.isConversion) {
                            return <span className="text-sm text-muted">-</span>;
                          }
                          const raw = event.concessions;
                          if (!raw || raw === 'No refund') return (<span className="text-sm text-muted">-</span>);
                          const num = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
                          if (!Number.isFinite(num) || num === 0) return (<span className="text-sm text-muted">-</span>);
                          const badgeClass = num < 0 ? 'badge badge-error' : 'badge badge-success';
                          const label = num > 0 ? `+${num}` : `${num}`;
                          const displayType = (!event.concessionType || event.concessionType === 'shared') ? '50' : event.concessionType;
                          const wallet = poolDisplayName(concessionTypes, displayType);
                          return (
                            <span className={`${badgeClass}`}>
                              {label}
                              <span className="text-xs ml-1 opacity-75">({wallet})</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td className="table-cell table-cell-center user-history-cell">
                        {(() => {
                          const running = runningTotalAt(event);
                          if (!Number.isFinite(running)) return <span className="text-sm text-muted">-</span>;
                          return <span className="text-sm font-medium text-primary">{running}</span>;
                        })()}
                      </td>
                      <td className="table-cell table-cell-center user-history-cell">
                        {(() => {
                          // Only show attendance for booking events that have a class date in the past
                          if (event.type === 'booking_created' || event.type === 'booking_attended' || event.type === 'admin_booking') {
                            const classDate = event.classDate;
                            const isPastClass = classDate && new Date(classDate) < new Date();
                            
                            if (isPastClass) {
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      color: event.status === 'no-show' ? '#ef4444' : '#10b981'
                                    }}>
                                      {event.status === 'no-show' ? 'No Show' : 'Attended'}
                                    </span>
                                    {event.status === 'no-show' && (
                                      <span style={{
                                        fontSize: '10px',
                                        color: 'var(--text-secondary)',
                                        fontStyle: 'italic'
                                      }}>
                                        (marked by admin)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            } else {
                              // Future class - show booking status
                              return (
                                <span style={{
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  color: event.status === 'confirmed' ? '#10b981' : '#f59e0b'
                                }}>
                                  {event.status === 'confirmed' ? 'Confirmed' : event.status || 'Booked'}
                                </span>
                              );
                            }
                          }
                          return <span className="text-sm text-muted">-</span>;
                        })()}
                      </td>
                      <td className="table-cell table-cell-right user-history-cell">
                        <span className="text-sm text-muted">
                          {formatEventTime(event.timestamp)}
                        </span>
                      </td>
                      </tr>
                      ))}
                    </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </>
          ) : (
            /* Add Entry Tab */
            <div className="p-6" style={{ maxWidth: '600px' }}>
              <h4 className="text-lg font-semibold text-primary mb-1">Add Manual History Entry</h4>
              <p className="text-sm text-secondary mb-6">
                This entry will appear in the user's history timeline at the specified date.
              </p>
              <form onSubmit={handleManualSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-primary mb-1">
                    Description <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={manualDescription}
                    onChange={(e) => setManualDescription(e.target.value)}
                    placeholder="e.g. Refund for missed session"
                    className="w-full px-3 py-2 border border-primary/20 rounded-lg bg-card text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-primary mb-1">
                    Date <span className="text-error">*</span>
                  </label>
                  <input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    className="w-full px-3 py-2 border border-primary/20 rounded-lg bg-card text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                  />
                  <p className="text-xs text-secondary mt-1">
                    The entry will be placed in the timeline at this date.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-primary mb-1">
                    Concession Impact
                  </label>
                  <input
                    type="number"
                    value={manualDelta}
                    onChange={(e) => setManualDelta(e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-primary/20 rounded-lg bg-card text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <p className="text-xs text-secondary mt-1">
                    Positive to add concessions, negative to deduct. Leave blank for no impact.
                  </p>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-primary mb-1">
                    Concession Wallet
                  </label>
                  <select
                    value={manualConcessionType}
                    onChange={(e) => setManualConcessionType(e.target.value)}
                    className="w-full px-3 py-2 border border-primary/20 rounded-lg bg-card text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  >
                    {concessionTypes.filter(t => !t.isShared).map((t) => (
                      <option key={t.key} value={t.key}>{t.displayName}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-primary mb-1">
                    Notes
                  </label>
                  <textarea
                    value={manualNotes}
                    onChange={(e) => setManualNotes(e.target.value)}
                    placeholder="Optional additional context..."
                    rows={3}
                    className="w-full px-3 py-2 border border-primary/20 rounded-lg bg-card text-primary text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-vertical"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !manualDescription.trim() || !manualDate}
                  className={`btn-primary flex items-center gap-2 ${submitting || !manualDescription.trim() || !manualDate ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <PlusIcon className={`h-4 w-4 ${submitting ? 'animate-spin' : ''}`} />
                  {submitting ? 'Adding...' : 'Add Entry'}
                </button>
              </form>
            </div>
          )}
        </div>
        
        {/* Footer with Action Buttons */}
        <div className="modal-footer-mobile">
          <div className="modal-footer-content">
            <button
              onClick={fetchUserHistory}
              disabled={loading}
              className={`btn-primary flex items-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
            <button
              onClick={exportToCSV}
              className="btn-primary flex items-center gap-2"
              >
              <DocumentArrowDownIcon className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserHistory;
